import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Cell,
    Column,
    HeaderGroup,
    Row,
    SortingRule,
    usePagination,
    useSortBy,
    UseSortByColumnProps,
    useTable,
} from 'react-table';
import { useDidMountEffect } from '../../hooks/UseDidMountEffect';
import { JRCIcon } from '../JRCIcon/JRCIcon';
import { JRCPagination } from '../JRCPagination/JRCPagination';

import type { Orders } from 'jamespot-user-api';
import styled from 'styled-components';
import { deepEqual } from '../../utils/utils.object';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCLoader } from '../JRCLoader/JRCLoader';
import { DraggableTBody, DraggableTr, LoadMore, Table, TBody, Td, Th, Thead, Tr } from './JRCList.styles';

export type LoadMorePagination = {
    type: 'loadMore';
    end: boolean;
};

export type PaginationPagination = {
    type?: 'pagination';
    page: number;
    perPage: number;
    nbResults: number;
};

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 50px;
`;

/**
 * Props type for JRCList
 * @member columns default react-table column props
 * @member data default react-table data props
 * @member eventHandlers event handlers: onDrag, onSort and onPage. You Must rearrange the array of data when these events are dispatched
 * @member config config object. See the Type for use
 * @member className className
 */
export type JRCListProps<T extends Record<string, unknown>> = {
    columns: Array<Column<T>>;
    data: Array<T>;
    eventHandlers?: {
        onDrag?: (src: T, target: T, position: 'before' | 'after', records: Array<T>) => void;
        onSort?: (orders: Orders) => void;
        onPage?: (page: number) => void;
    };
    config?: {
        manualSortBy?: boolean; // This props should be set to true if sorting is server-side made
        draggable?: boolean; // When set to true, the array elements MUST have a unique uri or type/id set attribute
        pagination?: LoadMorePagination | PaginationPagination;
        orders?: Orders;
    };
    className?: string;
    loading?: boolean;
    dataCy?: string;
};

type SortBys = Array<SortingRule<object>>;

function jOrdersToReactTableSortBy(orders?: Orders) {
    return orders ? orders.map((order) => ({ id: order.name, desc: order.sort === 'DESC' })) : [];
}

function reactTableSortByToJOrders(sortBy: SortBys): Orders {
    return sortBy.map((sortElement) => ({ name: sortElement.id, sort: sortElement.desc ? 'DESC' : 'ASC' }));
}

export const JRCList = <T extends Record<string, unknown>>(props: JRCListProps<T>) => {
    const [loadMore, setLoadMore] = React.useState(1);

    // Temporary record list used to show the real time order of the list while drag & drop
    const [dndRecords, setDndRecords] = React.useState<Array<T>>([]);

    const records = React.useMemo(() => (dndRecords.length ? dndRecords : props.data), [props.data, dndRecords]);
    const columns = React.useMemo(() => props.columns.map((c) => ({ ...c, width: c.width || -1 })), [props.columns]);
    const sortByState = React.useRef<SortBys>([]);

    const [dropIndex, setDropIndex] = React.useState(0);

    const draggable = props?.config?.draggable || false;

    const getRowId = React.useCallback((row, relativeIndex) => {
        if (row.uri) {
            return row.uri;
        } else if (row.id && row.type) {
            return `${row.type}/${row.id}`;
        } else {
            return relativeIndex;
        }
    }, []);

    const sortByProps = React.useMemo(() => {
        return jOrdersToReactTableSortBy(props.config?.orders);
    }, [props.config?.orders]);

    const tableInstance = useTable(
        {
            columns,
            data: records,
            manualSortBy: props?.config?.manualSortBy || false,
            initialState: {
                pageIndex: 0,
            },
            getRowId,
        },
        useSortBy,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { sortBy },
        setSortBy,
    } = tableInstance;

    useDidMountEffect(() => {
        if (props?.config?.manualSortBy && props.eventHandlers && props.eventHandlers.onSort) {
            props.eventHandlers.onSort(reactTableSortByToJOrders(sortBy));
            sortByState.current = sortBy;
        }
    }, [sortBy]);

    const onDrag = (dragIndex: number, dropIndex: number) => {
        if (dragStartIndex === undefined) {
            setDragStartIndex(dragIndex);
        }
        setDropIndex(dropIndex);

        const dragRecord = records[dragIndex];

        const newOrder = records.slice();
        newOrder.splice(dragIndex, 1);
        newOrder.splice(dropIndex, 0, dragRecord);

        setDndRecords(newOrder);
    };

    const [dragStartIndex, setDragStartIndex] = React.useState<number | undefined>();

    const onDrop = () => {
        if (dragStartIndex === undefined) {
            return;
        }

        const dragRecord = props.data[dragStartIndex];
        const dropRecord = props.data[dropIndex];
        const position = dropIndex - dragStartIndex < 0 ? 'before' : 'after';

        if (props.eventHandlers && props.eventHandlers.onDrag) {
            props.eventHandlers.onDrag(dragRecord, dropRecord, position, dndRecords);
        }

        setDndRecords([]);
        setDragStartIndex(undefined);
    };

    React.useEffect(() => {
        if (!deepEqual(sortByProps, sortByState.current)) setSortBy(sortByProps);
    }, [setSortBy, sortByProps]);

    return (
        <React.Fragment>
            <Table {...getTableProps()} data-cy={props.dataCy} className={props.className} loading={props.loading}>
                <Thead>
                    {headerGroups.map((headerGroup, idx) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id ?? `head-${idx}`}>
                            {draggable && <Th></Th>}
                            {headerGroup.headers.map((column: HeaderGroup<T> & UseSortByColumnProps<T>) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    key={column.id}
                                    title={undefined}
                                    width={column.width}>
                                    {column.render('Header')}
                                    {column.canSort ? (
                                        column.isSorted ? (
                                            column.isSortedDesc ? (
                                                <JRCIcon name="icon-sort-desc" size={12} color="grey4" />
                                            ) : (
                                                <JRCIcon name="icon-sort-asc" size={12} color="grey4" />
                                            )
                                        ) : (
                                            <JRCIcon name="icon-sort" size={12} color="grey2" />
                                        )
                                    ) : (
                                        ''
                                    )}
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>

                {!props.loading && (
                    <TBody {...getTableBodyProps()}>
                        <DraggableTBody draggable={draggable}>
                            {rows.map((row: Row<T>, index: number): any => {
                                prepareRow(row);
                                return (
                                    <DraggableTr
                                        index={index}
                                        onDrag={onDrag}
                                        onDrop={onDrop}
                                        draggable={draggable}
                                        {...row.getRowProps()}
                                        key={row.id}>
                                        {row.cells.map((cell: Cell<T>, i) => {
                                            return (
                                                <Td
                                                    {...cell.getCellProps()}
                                                    width={cell.column.width}
                                                    key={`${row.id}-${i}`}>
                                                    {cell.render('Cell')}
                                                </Td>
                                            );
                                        })}
                                    </DraggableTr>
                                );
                            })}
                        </DraggableTBody>
                    </TBody>
                )}
                {props.loading && (
                    <TBody {...getTableBodyProps()}>
                        <Tr key="loader">
                            <Td colSpan={headerGroups[0].headers.length + (draggable ? 1 : 0)}>
                                <LoaderWrapper>
                                    <JRCLoader size={'l'} />
                                </LoaderWrapper>
                            </Td>
                        </Tr>
                    </TBody>
                )}
            </Table>

            {props.config?.pagination && !props.loading ? (
                props.config.pagination?.type === 'loadMore' ? (
                    <>
                        {!props.config.pagination.end && (
                            <LoadMore>
                                <JRCButton
                                    onClick={() => {
                                        props.eventHandlers?.onPage?.(loadMore + 1);
                                        setLoadMore(loadMore + 1);
                                    }}>
                                    <FormattedMessage id="GLOBAL_Load_More" />
                                </JRCButton>
                            </LoadMore>
                        )}
                    </>
                ) : (
                    <JRCPagination
                        activePage={props.config.pagination.page}
                        nbrOfPage={Math.ceil(props.config.pagination.nbResults / props.config.pagination.perPage)}
                        clickHandler={(page) => props.eventHandlers?.onPage?.(page)}
                    />
                )
            ) : null}
        </React.Fragment>
    );
};
